import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database, UserGameStats, UserGameStatsUpdate, RankingEntry } from '@db/types/UserGameStats';
import { NotFoundError, ValidationError, handleSupabaseError } from '@db/utils/errors';
import { createUserProfile } from './profile.service';
import { SEASON_TABLE_NAME } from '@/consts/season';

export type SaveGameResultResponse = {
  highScore: number;
  isNewHighScore: boolean;
};

/**
 * JOIN結果からuser_nameを安全に抽出する
 */
function extractUserNameFromEntry(entry: unknown, userId: string): string {
  if (typeof entry !== 'object' || entry === null) {
    throw new ValidationError(`ユーザーID ${userId} のデータが不正です`);
  }

  // user_profilesフィールドの存在をチェック
  if (!('user_profiles' in entry)) {
    throw new ValidationError(`ユーザーID ${userId} のプロフィールが見つかりません`);
  }

  const profile = entry.user_profiles;
  if (profile === null) {
    throw new ValidationError(`ユーザーID ${userId} のプロフィールが見つかりません`);
  }

  if (typeof profile !== 'object') {
    throw new ValidationError(`ユーザーID ${userId} のプロフィールの型が不正です`);
  }

  // user_nameフィールドの存在をチェック
  if (!('user_name' in profile)) {
    throw new ValidationError(`ユーザーID ${userId} のプロフィールにuser_nameがありません`);
  }

  const userName = profile.user_name;
  if (typeof userName !== 'string') {
    throw new ValidationError(`ユーザーID ${userId} のプロフィールのuser_nameが文字列ではありません`);
  }

  if (userName === '') {
    throw new ValidationError(`ユーザーID ${userId} のプロフィールのuser_nameが空です`);
  }

  return userName;
}

/**
 * ユーザーの統計データを取得
 *
 * @param supabase - Supabaseクライアント
 * @param userId - ユーザーID
 * @returns ユーザーの統計データ、存在しない場合null
 *
 * @example
 * ```typescript
 * const stats = await getUserStats(supabase, userId);
 * if (stats) {
 *   console.log('ハイスコア:', stats.high_score);
 * }
 * ```
 */
export async function getUserStats(
  supabase: SupabaseClient<Database>,
  userId: string
): Promise<UserGameStats | null> {
  if (!userId) {
    throw new ValidationError('ユーザーIDが指定されていません');
  }

  const { data, error } = await supabase
    .from(SEASON_TABLE_NAME)
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    handleSupabaseError(error);
  }

  return data;
}

/**
 * 統計データを作成(初回プレイ時)
 *
 * @param supabase - Supabaseクライアント
 * @param userId - ユーザーID
 * @returns 作成された統計データ
 *
 * @example
 * ```typescript
 * const stats = await createUserStats(supabase, userId);
 * ```
 */
export async function createUserStats(
  supabase: SupabaseClient<Database>,
  userId: string
): Promise<UserGameStats> {
  if (!userId) {
    throw new ValidationError('ユーザーIDが指定されていません');
  }

  // 既存データがあるかチェック
  const existing = await getUserStats(supabase, userId);
  if (existing) {
    return existing; // 既に存在する場合はそれを返す
  }

  // プロフィールも同時に作成（初回ゲームプレイ時）
  await createUserProfile(supabase, userId);

  const { data, error } = await supabase
    .from(SEASON_TABLE_NAME)
    .insert({
      user_id: userId,
      high_score: 0,
      block_01: 0,
      block_02: 0,
      block_03: 0,
      block_04: 0,
      block_05: 0,
      block_06: 0,
      block_07: 0,
      block_08: 0,
      block_09: 0,
      block_10: 0,
    })
    .select()
    .single();

  if (error) {
    // 競合で他リクエストが先に作成した場合は既存行を返す（get-or-create）
    if (error.code === '23505') {
      const existing = await getUserStats(supabase, userId);
      if (existing) return existing;
    }
    handleSupabaseError(error);
  }

  if (!data) {
    throw new NotFoundError('統計データの作成に失敗しました');
  }

  return data;
}

/**
 * ハイスコアを更新(現在のスコアより高い場合のみ)
 *
 * @param supabase - Supabaseクライアント
 * @param userId - ユーザーID
 * @param newScore - 新しいスコア
 * @returns 更新後のハイスコア
 *
 * @example
 * ```typescript
 * const highScore = await updateHighScore(supabase, userId, 5000);
 * console.log('新しいハイスコア:', highScore);
 * ```
 */
export async function updateHighScore(
  supabase: SupabaseClient<Database>,
  userId: string,
  newScore: number
): Promise<number> {
  if (!userId) {
    throw new ValidationError('ユーザーIDが指定されていません');
  }

  if (typeof newScore !== 'number' || newScore < 0) {
    throw new ValidationError('スコアは0以上の数値である必要があります');
  }

  // 現在の統計データを取得
  const currentStats = await getUserStats(supabase, userId);

  // データが存在しない場合は作成
  if (!currentStats) {
    await createUserStats(supabase, userId);
    return await updateHighScore(supabase, userId, newScore);
  }

  // 現在のハイスコアより高い場合のみ更新
  if (newScore > currentStats.high_score) {
    const { data, error } = await supabase
      .from(SEASON_TABLE_NAME)
      .update({ high_score: newScore })
      .eq('user_id', userId)
      .select('high_score')
      .single();

    if (error) {
      handleSupabaseError(error);
    }

    return data?.high_score ?? currentStats.high_score;
  }

  return currentStats.high_score;
}

/**
 * ブロック消去回数を増加
 *
 * @param supabase - Supabaseクライアント
 * @param userId - ユーザーID
 * @param blockNumber - ブロック番号(1~10)
 * @param count - 増加させる回数(デフォルト: 1)
 *
 * @example
 * ```typescript
 * // block_03を5回消去したことを記録
 * await incrementBlockCount(supabase, userId, 3, 5);
 * ```
 */
export async function incrementBlockCount(
  supabase: SupabaseClient<Database>,
  userId: string,
  blockNumber: number,
  count: number = 1
): Promise<void> {
  if (!userId) {
    throw new ValidationError('ユーザーIDが指定されていません');
  }

  if (blockNumber < 1 || blockNumber > 10) {
    throw new ValidationError('ブロック番号は1~10の範囲である必要があります');
  }

  if (typeof count !== 'number' || count < 0) {
    throw new ValidationError('カウントは0以上の数値である必要があります');
  }

  // ブロックカラム名を生成
  const blockColumn = `block_${String(blockNumber).padStart(2, '0')}` as keyof UserGameStats;

  // 現在の統計データを取得
  const currentStats = await getUserStats(supabase, userId);

  // データが存在しない場合は作成
  if (!currentStats) {
    await createUserStats(supabase, userId);
    return await incrementBlockCount(supabase, userId, blockNumber, count);
  }

  const currentCount = currentStats[blockColumn] as number;
  const newCount = currentCount + count;

  const updatePayload = { [blockColumn]: newCount };
  const { error } = await supabase
    .from(SEASON_TABLE_NAME)
    .update(updatePayload)
    .eq('user_id', userId);

  if (error) {
    handleSupabaseError(error);
  }
}

/**
 * 統計データを一括更新
 *
 * @param supabase - Supabaseクライアント
 * @param userId - ユーザーID
 * @param updates - 更新するフィールド
 *
 * @example
 * ```typescript
 * await updateStats(supabase, userId, {
 *   high_score: 15000,
 *   block_01: 100,
 *   block_02: 90,
 * });
 * ```
 */
export async function updateStats(
  supabase: SupabaseClient<Database>,
  userId: string,
  updates: UserGameStatsUpdate
): Promise<void> {
  if (!userId) {
    throw new ValidationError('ユーザーIDが指定されていません');
  }

  if (!updates || Object.keys(updates).length === 0) {
    throw new ValidationError('更新するデータが指定されていません');
  }

  // 現在の統計データを取得
  const currentStats = await getUserStats(supabase, userId);

  // データが存在しない場合は作成
  if (!currentStats) {
    await createUserStats(supabase, userId);
  }

  const { error } = await supabase
    .from(SEASON_TABLE_NAME)
    .update(updates)
    .eq('user_id', userId);

  if (error) {
    handleSupabaseError(error);
  }
}

/**
 * ハイスコアランキングを取得(上位N件)
 *
 * @param supabase - Supabaseクライアント
 * @param limit - 取得件数(デフォルト: 10)
 * @returns ランキングデータ配列
 *
 * @example
 * ```typescript
 * const ranking = await getTopScores(supabase, 10);
 * ranking.forEach((entry, index) => {
 *   console.log(`${index + 1}位: ${entry.user_name} - ${entry.high_score}点`);
 * });
 * ```
 */
export async function getTopScores(
  supabase: SupabaseClient<Database>,
  limit: number = 10
): Promise<RankingEntry[]> {
  if (typeof limit !== 'number' || limit < 1) {
    throw new ValidationError('limitは1以上の数値である必要があります');
  }

  const { data, error } = await supabase
    .from(SEASON_TABLE_NAME)
    .select('*, user_profiles!inner(user_name)')
    .order('high_score', { ascending: false })
    .limit(limit);

  if (error) {
    handleSupabaseError(error);
  }

  if (!data) {
    return [];
  }

  // ランク情報とuser_nameを追加
  return data.map((entry, index): RankingEntry => {
    // entryがUserGameStatsの基本構造を持っているかチェック
    if (
      typeof entry !== 'object' ||
      entry === null ||
      typeof entry.user_id !== 'string' ||
      typeof entry.high_score !== 'number' ||
      typeof entry.block_01 !== 'number' ||
      typeof entry.block_02 !== 'number' ||
      typeof entry.block_03 !== 'number' ||
      typeof entry.block_04 !== 'number' ||
      typeof entry.block_05 !== 'number' ||
      typeof entry.block_06 !== 'number' ||
      typeof entry.block_07 !== 'number' ||
      typeof entry.block_08 !== 'number' ||
      typeof entry.block_09 !== 'number' ||
      typeof entry.block_10 !== 'number'
    ) {
      throw new ValidationError(`ランキングデータの型が不正です（インデックス: ${index}）`);
    }

    // user_nameを安全に抽出
    const userName = extractUserNameFromEntry(entry, entry.user_id);

    return {
      high_score: entry.high_score,
      rank: index + 1,
      user_id: entry.user_id,
      user_name: userName,
    };
  });
}

/**
 * 特定ユーザーのランクを取得
 *
 * @param supabase - Supabaseクライアント
 * @param userId - ユーザーID
 * @returns ユーザーのランク(1位が1)、データがない場合null
 *
 * @example
 * ```typescript
 * const rank = await getUserRank(supabase, userId);
 * if (rank) {
 *   console.log(`あなたは${rank}位です`);
 * }
 * ```
 */
export async function getUserRank(
  supabase: SupabaseClient<Database>,
  userId: string
): Promise<number | null> {
  if (!userId) {
    throw new ValidationError('ユーザーIDが指定されていません');
  }

  const userStats = await getUserStats(supabase, userId);
  if (!userStats) {
    return null;
  }

  // 自分より高いスコアの数を数える
  const { count, error } = await supabase
    .from(SEASON_TABLE_NAME)
    .select('*', { count: 'exact', head: true })
    .gt('high_score', userStats.high_score);

  if (error) {
    handleSupabaseError(error);
  }

  return (count ?? 0) + 1;
}

/**
 * ゲーム結果を保存（ハイスコア更新時のみブロック消去数も一緒に保存）
 *
 * @param supabase - Supabaseクライアント
 * @param userId - ユーザーID
 * @param newScore - 今回のスコア
 * @param blockCounts - ブロック消去数（block_01〜block_10）
 * @returns ハイスコアと更新有無
 *
 * @example
 * ```typescript
 * const result = await saveGameResult(supabase, userId, 5000, { block_01: 3, block_02: 1 });
 * if (result.isNewHighScore) {
 *   console.log('ハイスコア更新！', result.highScore);
 * }
 * ```
 */
export async function saveGameResult(
  supabase: SupabaseClient<Database>,
  userId: string,
  newScore: number,
  blockCounts: Record<string, number>,
): Promise<SaveGameResultResponse> {
  if (!userId) {
    throw new ValidationError('ユーザーIDが指定されていません');
  }

  if (typeof newScore !== 'number' || newScore < 0) {
    throw new ValidationError('スコアは0以上の数値である必要があります');
  }

  const currentStats = await getUserStats(supabase, userId);

  if (!currentStats) {
    await createUserStats(supabase, userId);
    return await saveGameResult(supabase, userId, newScore, blockCounts);
  }

  if (newScore > currentStats.high_score) {
    const updatePayload: UserGameStatsUpdate = {
      high_score: newScore,
      ...blockCounts,
    };

    const { data, error } = await supabase
      .from(SEASON_TABLE_NAME)
      .update(updatePayload)
      .eq('user_id', userId)
      .select('high_score')
      .single();

    if (error) {
      handleSupabaseError(error);
    }

    return {
      highScore: data?.high_score ?? newScore,
      isNewHighScore: true,
    };
  }

  return {
    highScore: currentStats.high_score,
    isNewHighScore: false,
  };
}

/**
 * 統計データが存在するかチェック
 *
 * @param supabase - Supabaseクライアント
 * @param userId - ユーザーID
 * @returns データが存在する場合true
 *
 * @example
 * ```typescript
 * if (!await statsExists(supabase, userId)) {
 *   await createUserStats(supabase, userId);
 * }
 * ```
 */
export async function statsExists(
  supabase: SupabaseClient<Database>,
  userId: string
): Promise<boolean> {
  const stats = await getUserStats(supabase, userId);
  return stats !== null;
}
